import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { GoogleAnalyticsService, WordpressVideosService } from '@app/services/_index';
import { HasAnalytics, VideoInterface } from '@app/interfaces/_index';

import { Subscription } from 'rxjs';
import { Video } from '@app/models/_index';
import { VideoAdapter } from '@app/adapters/video.adapter';
import { environment } from '@environments/environment';
import { logGoogleAnalytics } from '@app/utilities/_index';

@Component({
  selector: 'app-featured-videos',
  templateUrl: './featured-videos.component.html',
  styleUrls: ['./featured-videos.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class FeaturedVideosComponent implements OnInit, OnDestroy, HasAnalytics {
  isLoading: boolean;
  featuredVideos: Video[] = [];
  recommendedVideos: Video[] = [];
  subs: Subscription[] = [];
  @Input() isHomePageRecommendedVideos: boolean;
  homeRecommendedVideoIds: number[] = environment.beyond.home_page.showRecommendedVideos;
  constructor(
    private videoAdapter: VideoAdapter,
    private googleAnalyticsService: GoogleAnalyticsService,
    private wpVideoService: WordpressVideosService
  ) {}

  /**
   * OnInit Load default 1 featured video and 2 recommended videos for the home page
   */
  ngOnInit(): void {
    // WordPress CMS
    this.getWpFeaturedVideos();
    this.getWpRecommendedVideos();
  }

  /**
   * On Destroy unsubscribe the services to prevent memory leaks
   */
  ngOnDestroy(): void {
    if (this.subs) {
      this.subs.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }

  /**
   * Load Recommended Videos from wordpress
   * TODO: Refactor this to combine with recommended video with this duplicate code.
   */
  getWpRecommendedVideos(): void {
    this.isLoading = true;
    this.subs.push(
      this.wpVideoService.getWpRecommendedVideos().subscribe({
        next: (videos: VideoInterface[]) => {
          videos.forEach((item: VideoInterface) => {
            const video = this.videoAdapter.adapt(item);
            const videoCategories = video.categories;
            const isFeaturedVideo = videoCategories.some(
              (i) => i.slug && i.slug.includes('featured')
            );
            if (!isFeaturedVideo) {
              const videoId = Number(video.id);
              if (
                (this.isHomePageRecommendedVideos &&
                  Number(videoId) === environment.beyond.home_page.showRecommendedVideos[0]) ||
                Number(videoId) === environment.beyond.home_page.showRecommendedVideos[1]
              ) {
                this.recommendedVideos.push(video);
              }
            }
          });
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Recommended Videos content completed');
        },
      })
    );
  }

  /**
   * Load Featured Videos from Wordpress
   * TODO: Refactor this to combine with recommended video with this duplicate code.
   */
  getWpFeaturedVideos(): void {
    this.isLoading = true;
    this.subs.push(
      this.wpVideoService.getWpFeaturedVideos().subscribe({
        next: (videos: VideoInterface[]) => {
          videos.forEach((item: VideoInterface) => {
            const video = this.videoAdapter.adapt(item);
            const videoCategories = video.categories;
            const isFeaturedVideo = videoCategories.some(
              (i) => i.slug && i.slug.includes('featured')
            );
            if (isFeaturedVideo) {
              this.featuredVideos.push(video);
            }
          });
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Featured Videos content completed');
        },
      })
    );
  }

  /**
   * Send the query search label to Google Analytics
   * @param category - the source of the google analytics category string (ex. the page or component triggering the action)
   * @param action - the action to be (ex. click)
   * @param dataLabel - the label associated with the action
   */
  sendGoogleAnalytics(category: string, action: string, dataLabel: string): void {
    logGoogleAnalytics(category, action, dataLabel);
    this.googleAnalyticsService.sendEvent(category, action, dataLabel);
  }

  /**
   * Add Track By Featured Video Id to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of Featured Video array
   * @param item - Featured Video item
   * @returns Featured Video id
   */
  trackByFeaturedVideoId(index: number, item: Video): string {
    if (item) {
      return item.id;
    } else {
      return String(index);
    }
  }

  /**
   * Add Track By Recommended Video Id to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of Recommended Video array
   * @param item - Recommended Video item
   * @returns Recommended Video id
   */
  trackByRecommendedVideoId(index: number, item: Video): string {
    if (item) {
      return item.id;
    } else {
      return String(index);
    }
  }
}
